import React, { useMemo } from "react";
import styled from "styled-components";
import { useSelector, useStore } from "react-redux";
import i18n from "i18next";

import {
  currentPortfolioSelector,
  shortFormatNumberWithCurrency,
  formatNumberWithKuberaNumberFormatSettingsSelector
} from "@kubera/common";

import { getLinkedDebtsRelatedValues } from "utilities/CustomHooks";

const LinkDisplays = `
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  border-radius: 0.25rem;
  height: 1.8125rem;
  box-sizing: border-box;
`;

const LinkDisplayContainer = styled.div`
  ${LinkDisplays}
  display: flex;
  margin-left: 0.875rem;
`;

const EquityContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: ${props => props.theme.linkedIconEquityContainerBG};
  font-weight: 500;
  font-size: 0.6875rem;
  line-height: 0.8125rem;
  color: ${props => props.theme.linkedIconEquityContainerCLR};
  text-transform: uppercase;
  padding: 0 0.1875rem;
  white-space: nowrap;
`;

const EquityText = styled.div`
  font-weight: 500;
  font-size: 0.5625rem;
  line-height: 0.6875rem;
  letter-spacing: -0.02em;
`;

const LinkedDebtContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: 2.5rem;
  margin-top: 1.25rem;
`;

const LinkedDebtTitle = styled.div`
  font-size: 0.6875rem;
  leading-trim: NONE;
  line-height: 100%;
  letter-spacing: 1%;
  font-variant-numeric-figure: lining-nums;
  font-variant-numeric-spacing: proportional-nums;
  text-transform: uppercase;
`;

const LinkedDebtValue = styled.div`
  font-style: Regular;
  font-size: 1.25rem;
  leading-trim: NONE;
  line-height: 130%;
  letter-spacing: 0%;
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 3px;
  background: #f2f2f2;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
`;

const BadgeDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: 125%;
  font-feature-settings: "ss01" on, "calt" off;
  color: rgba(36, 36, 36, 0.6);
  white-space: pre-wrap;
  text-transform: uppercase;
`;

const BadgeValue = styled.div`
  white-space: nowrap;
`;

const BadgeLabel = styled.div`
  font-size: 9px;
  white-space: nowrap;
`;

const EquityBadge = ({
  showForDetails = false,
  showForChildDetails = false,
  relatedId,
  currency,
  currencyCellValue,
  showLTV = false
}) => {
  const store = useStore();

  const portfolio = useSelector(currentPortfolioSelector);
  const linkedDebtValues = useMemo(() => {
    return getLinkedDebtsRelatedValues(store.getState(), portfolio, relatedId, currency, currencyCellValue);
  }, [portfolio.currency, portfolio.details, relatedId, currency, currencyCellValue]);

  const equity = linkedDebtValues?.[0] ? linkedDebtValues[0] : null;
  const ltv = linkedDebtValues?.[1]
    ? formatNumberWithKuberaNumberFormatSettingsSelector(store.getState(), linkedDebtValues[1]) + "%"
    : null;
  const debtValue = linkedDebtValues?.[2]
    ? shortFormatNumberWithCurrency(store.getState(), linkedDebtValues[2], portfolio.currency, true, true)
    : null;

  if (equity) {
    if (showForDetails) {
      return (
        <BadgeContainer>
          <BadgeDetails>
            <div>
              <BadgeValue>{equity}</BadgeValue>
              <BadgeLabel>{i18n.t("equity")}</BadgeLabel>
            </div>
          </BadgeDetails>
        </BadgeContainer>
      );
    }

    if (showForChildDetails) {
      return (
        <LinkedDebtContainer>
          <div>
            <LinkedDebtTitle>{i18n.t("equity")}</LinkedDebtTitle>
            <LinkedDebtValue>{equity}</LinkedDebtValue>
          </div>
          <div>
            <LinkedDebtTitle>{i18n.t("linkAccount.linkedDebt")}</LinkedDebtTitle>
            <LinkedDebtValue>{debtValue}</LinkedDebtValue>
          </div>
        </LinkedDebtContainer>
      );
    }

    return (
      <LinkDisplayContainer>
        <EquityContainer>
          {equity}
          <br />
          <EquityText>{i18n.t("equity")}</EquityText>
        </EquityContainer>
        {showLTV && ltv && (
          <EquityContainer>
            {ltv}
            <br />
            <EquityText>{i18n.t("LTV")}</EquityText>
          </EquityContainer>
        )}
      </LinkDisplayContainer>
    );
  }

  return null;
};

export default EquityBadge;
